<template>
  <Teleport to="body">
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <h3>Insert Link</h3>
        <div class="modal-body">
          <label for="linkUrl">URL:</label>
          <input
            type="text"
            id="linkUrl"
            v-model="linkUrl"
            placeholder="https://example.com"
            @keyup.enter="insertLink"
          />

          <label for="linkText" class="mt-3">Text (optional):</label>
          <input
            type="text"
            id="linkText"
            v-model="linkText"
            placeholder="Display text"
            @keyup.enter="insertLink"
          />

          <div class="checkbox-wrapper">
            <input type="checkbox" id="newTab" v-model="openInNewTab" />
            <label for="newTab" class="checkbox-label">Open in new tab</label>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn-cancel">Cancel</button>
          <button @click="insertLink" :disabled="!linkUrl">Insert</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { defineEmits, defineProps, ref, watch } from "vue";

const props = defineProps({
  editor: {
    type: Object,
    required: true,
  },
  showModal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

const linkUrl = ref("");
const linkText = ref("");
const openInNewTab = ref(false);

// Reset form on modal open
watch(
  () => props.showModal,
  (newVal) => {
    if (newVal) {
      // Get current selection details
      const { editor } = props;
      const currentLink = editor.getAttributes("link");

      // If a link is already selected, populate fields with its values
      if (currentLink.href) {
        linkUrl.value = currentLink.href;
        openInNewTab.value = currentLink.target === "_blank";
      } else {
        // Otherwise use defaults
        linkUrl.value = "";
        openInNewTab.value = true;
      }

      // Get selected text for optional text field
      linkText.value = editor.view.state.doc.textBetween(
        editor.state.selection.from,
        editor.state.selection.to,
        " "
      );
    }
  }
);

const insertLink = () => {
  if (!linkUrl.value) {
    return;
  }

  const { editor } = props;
  const attrs = {
    href: linkUrl.value,
  };

  if (openInNewTab.value) {
    attrs.target = "_blank";
    attrs.rel = "noopener noreferrer";
  }

  // If there's text in the text field and no text is selected, insert new text with link
  if (linkText.value && editor.state.selection.empty) {
    editor
      .chain()
      .focus()
      .insertContent({
        type: "text",
        text: linkText.value,
        marks: [{ type: "link", attrs }],
      })
      .run();
  } else {
    // Otherwise apply link to selected text
    editor.chain().focus().extendMarkRange("link").setLink(attrs).run();
  }

  closeModal();
};

const closeModal = () => {
  emit("close");
};
</script>

<style scoped>
/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 500px;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.25em;
  color: #343a40;
  text-align: center;
}

.modal-body {
  margin-bottom: 20px;
}

.modal-body label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #495057;
}

.modal-body input[type="text"] {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-sizing: border-box;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  margin-top: 15px;
}

.checkbox-label {
  margin-left: 8px;
  margin-bottom: 0;
}

.mt-3 {
  margin-top: 15px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.modal-footer button {
  padding: 8px 15px;
  border-radius: 4px;
  border: 1px solid #ced4da;
  cursor: pointer;
  font-size: 0.9em;
}

.modal-footer button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-footer button.btn-cancel {
  background-color: #f8f9fa;
  color: #495057;
}
.modal-footer button.btn-cancel:hover {
  background-color: #e9ecef;
}

.modal-footer button:not(.btn-cancel) {
  background-color: var(--primary-blue);
  color: white;
  border-color: var(--primary-blue);
}
.modal-footer button:not(.btn-cancel):not(:disabled):hover {
  background-color: var(--primary-blue-dark);
  border-color: var(--primary-blue-dark);
}
</style>
